import classNames from 'classnames'
import React from 'react'

import { Link as RouterLink } from '@lib/react-router-dom'

import { BaseComponentProps } from '@ui/common/component.props'
import { IconFCType } from '@ui/icons/types'

import styles from './Link.module.scss'

type RouterLinkProps = React.ComponentProps<typeof RouterLink>

export type LinkSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
export type LinkVariant = 'interactive' | 'secondary' | 'tertiary'

const getIconSize = (size: LinkSize): number => ({ xs: 14, sm: 16, md: 18, lg: 20, xl: 24 }[size])

const getIconColor = (variant: LinkVariant) => {
  switch (variant) {
    case 'interactive':
      return 'text-info-med-em'
    case 'secondary':
      return 'text-med-em'
    case 'tertiary':
      return 'text-purple-high-em'
    default:
      return 'text-info-med-em'
  }
}

interface LinkCommonProps extends Omit<BaseComponentProps<HTMLAnchorElement>, 'onClick'> {
  variant?: LinkVariant
  size?: LinkSize
  underline?: boolean
  fullWidth?: boolean
  disabled?: boolean
  iconLeft?: IconFCType
  iconRight?: IconFCType
  target?: React.HTMLAttributeAnchorTarget
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  children?: React.ReactNode
  download?: string
  wrapChildren?: boolean
}

interface AnchorLinkProps extends LinkCommonProps {
  href?: string
  to?: never
  state?: never
}

interface RouterNavigationProps extends LinkCommonProps {
  to?: RouterLinkProps['to']
  state?: RouterLinkProps['state']
  href?: never
}

export type LinkProps = AnchorLinkProps | RouterNavigationProps

export const Link: React.FC<LinkProps> = (props) => {
  const {
    href,
    to,
    state,
    download,
    variant = 'interactive',
    size = 'md',
    fullWidth = false,
    disabled = false,
    underline = true,
    iconLeft: IconLeft,
    iconRight: IconRight,
    children,
    target = '_blank',
    onClick,
    wrapChildren = true,
    ...restProps
  } = props

  const iconSize = getIconSize(size)
  const iconColor = getIconColor(variant)

  const content = (
    <>
      {IconLeft && (
        <span className={styles.iconLeft}>
          <IconLeft color={iconColor} size={iconSize} />
        </span>
      )}

      {wrapChildren ? (
        <span
          className={classNames({
            [styles.fullWidth]: fullWidth,
          })}
        >
          {children}
        </span>
      ) : (
        children
      )}

      {IconRight && (
        <span className={styles.iconRight}>
          <IconRight color={iconColor} size={iconSize} />
        </span>
      )}
    </>
  )

  const classes = classNames(styles.link, styles[`size-${size}`], styles[`variant-${variant}`], {
    [styles.noUnderline]: !underline,
    [styles.fullWidth]: fullWidth,
  })

  if (disabled) {
    return (
      <span className={classes} {...restProps}>
        {content}
      </span>
    )
  }

  if (to) {
    return (
      <RouterLink className={classes} to={to} state={state} onClick={onClick} {...restProps}>
        {content}
      </RouterLink>
    )
  }

  return (
    <a
      className={classNames(styles.link, styles[`size-${size}`], styles[`variant-${variant}`], {
        [styles.noUnderline]: !underline,
        [styles.fullWidth]: fullWidth,
      })}
      href={href}
      target={target}
      rel='noopener noreferrer'
      download={download}
      onClick={onClick}
      {...restProps}
    >
      {content}
    </a>
  )
}
